import { Stack } from '@mui/material';
import type {
  BackupVault,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import React, { useEffect, useState } from 'react';

import { isNativeBackupResource } from '@/app/(dashboard)/inventory/inventoryResourceUtils';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { dayjs } from '@/utils/dayjs';

import {
  ContinuousSnapshots,
  type ContinuousSnapshot,
} from './continuousSnapshots';
import { EfsSnapshots } from './efsSnapshots';
import type { MutateSnapshots } from './eonSnapshotsTab';
import { FsxSnapshots } from './fsxSnapshots';
import { NativeDbSnapshots } from './nativeDbSnapshots';
import { PitrSnapshots } from './pitrSnapshots';
import { RedshiftSnapshots } from './redshiftSnapshots';
import { getRestoreWizardFactory } from './restoreWizardFactory';
import { SnapshotDrawerHeader } from './snapshotDrawerHeader';
import type { SnapshotsOrigin } from './snapshotOriginToggleButtons';
import { SnapshotsRows } from './snapshotsRows';
import {
  useCallbackSnapshotsTypesTabs,
  type SnapshotsType,
} from './snapshotsTypesTabs';
import { useDateSnapshots } from './useDateSnapshots';
import { VmBasedSnapshots } from './vmBasedSnapshots';

import { ConvertSnapshotsList } from '../../../../snapshots/convertToEon/convertSnapshotsList';

const VmBasedResourceTypes: ResourceType[] = [
  ResourceType.AwsEc2,
  ResourceType.AzureVirtualMachine,
  ResourceType.AzureSqlVirtualMachine,
  ResourceType.AzureSapHanaVm,
  ResourceType.GcpSapHanaVm,
  ResourceType.GcpComputeEngineInstance,
];

export const RestoreSnapshotDrawer = ({
  vaults,
  eonSnapshots,
  hasProviderSnapsInDate,
  showOtherSnapshots,
  continuousSnapshots,
  selectedDate,
  entity,
  onClose,
  mutateSnapshots,
}: {
  vaults: BackupVault[];
  eonSnapshots: Snapshot[];
  hasProviderSnapsInDate: boolean;
  showOtherSnapshots: boolean;
  continuousSnapshots: ContinuousSnapshot[];
  selectedDate: Date;
  entity: InventoryResource;
  onClose: () => void;
  mutateSnapshots: MutateSnapshots;
}) => {
  const { AzureSnapshotsDiscoverySupport } = useFeatureFlags();

  const isBigQueryNativePitr = entity.resourceType === ResourceType.GcpBigQuery;

  const isContinuousSupported = continuousSnapshots.some(
    (snapshot) =>
      snapshot.start &&
      snapshot.end &&
      dayjs
        .utc(selectedDate)
        .endOf('day')
        .isAfter(dayjs.utc(snapshot.start).startOf('day')) &&
      dayjs
        .utc(selectedDate)
        .startOf('day')
        .isBefore(dayjs.utc(snapshot.end).endOf('day'))
  );

  const [selectedOrigin, setSelectedOrigin] = useState<SnapshotsOrigin>(
    hasProviderSnapsInDate ? 'other' : 'eon'
  );
  const isVmBaseResourceType = VmBasedResourceTypes.includes(
    entity.resourceType
  );
  const isNativeBackup = isNativeBackupResource(entity);
  const getSnapshotType = useCallbackSnapshotsTypesTabs(
    isVmBaseResourceType,
    isContinuousSupported,
    isBigQueryNativePitr
  );
  const [snapshotType, setSnapshotType] = useState<SnapshotsType | undefined>(
    getSnapshotType()
  );

  const selectedDateSnapshots = useDateSnapshots(eonSnapshots, selectedDate);

  useEffect(() => {
    if (selectedDateSnapshots.length <= 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedOrigin('other');
    }
  }, [selectedDateSnapshots]);

  const shouldShowGroupButtons = !!(
    selectedDateSnapshots.length &&
    hasProviderSnapsInDate &&
    showOtherSnapshots
  );

  const snapshotsWithPiTR = selectedDateSnapshots.filter(
    (snap) => snap.supportPiTR
  );

  const shouldShowSnapsTypeTabs =
    (isVmBaseResourceType && snapshotsWithPiTR.length > 0) ||
    isContinuousSupported;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSnapshotType(getSnapshotType());
  }, [getSnapshotType, shouldShowSnapsTypeTabs]);

  const isContainerHeightFixed =
    snapshotType === 'database-pitr' && !isContinuousSupported;

  return (
    <Stack
      maxHeight={isContainerHeightFixed ? 'none' : '60%'}
      flexBasis={isContainerHeightFixed ? '60%' : 'fit-content'}
      flexGrow={isContainerHeightFixed ? '0' : undefined}
      flexShrink={isContainerHeightFixed ? '0' : undefined}
      overflow={isContainerHeightFixed ? 'hidden' : undefined}
      data-testid='restore-snapshot-drawer'
      className='w-full'
      direction='column'
      boxShadow='var(--mui-customShadows-panelMd)'
    >
      <Stack
        overflow='hidden'
        height='100%'
        className='w-full'
        direction='column'
      >
        <SnapshotDrawerHeader
          selectedDate={selectedDate}
          shouldShowGroupButtons={shouldShowGroupButtons}
          onSnapshotTypeSelected={setSnapshotType}
          shouldShowSnapsTypeTabs={shouldShowSnapsTypeTabs}
          snapshotType={snapshotType}
          onClose={onClose}
          onSnapshotOriginSelected={setSelectedOrigin}
          selectedOrigin={selectedOrigin}
          resourceType={entity.resourceType}
        />
        <Stack
          direction='column'
          alignItems='center'
          width='100%'
          minHeight='0'
          flexGrow='1'
        >
          {selectedOrigin === 'other' &&
          showOtherSnapshots &&
          hasProviderSnapsInDate &&
          (entity.resourceType === ResourceType.AwsEc2 ||
            isNativeBackup ||
            (entity.resourceType === ResourceType.AzureVirtualMachine &&
              AzureSnapshotsDiscoverySupport)) ? (
            <ConvertSnapshotsList
              data-testid='other-snapshots-tab'
              resource={entity}
              selectedDate={selectedDate}
            />
          ) : (
            <RestoreSnapshotsList
              snapshotType={snapshotType}
              dateSnapshots={selectedDateSnapshots}
              entity={entity}
              selectedDate={selectedDate}
              isContinuousSupported={isContinuousSupported}
              continuousSnapshots={continuousSnapshots}
              vaults={vaults}
              mutateSnapshots={mutateSnapshots}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const RestoreSnapshotsList = ({
  snapshotType,
  entity,
  dateSnapshots,
  selectedDate,
  isContinuousSupported,
  continuousSnapshots,
  vaults,
  mutateSnapshots,
}: {
  snapshotType: SnapshotsType | undefined;
  entity: InventoryResource;
  dateSnapshots: Snapshot[];
  selectedDate: Date;
  isContinuousSupported: boolean;
  continuousSnapshots: ContinuousSnapshot[];
  vaults: BackupVault[];
  mutateSnapshots: MutateSnapshots;
}) => {
  const restoreWizardFactory = getRestoreWizardFactory(entity.resourceType);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState('');
  const isNativeBackup = isNativeBackupResource(entity);
  const snapshotsWithPiTR = dateSnapshots.filter((snap) => snap.supportPiTR);
  const snapshotsWithoutPiTR = dateSnapshots.filter(
    (snap) => !snap.supportPiTR
  );

  useEffect(() => {
    if (!snapshotType || snapshotType === 'vm-based') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSnapshot(
        dateSnapshots.find((snap) => !snap.supportPiTR)?.id || ''
      );
    } else if (snapshotType === 'database-pitr') {
      const snapshotsWithPiTR = dateSnapshots.filter(
        (snap) => snap.supportPiTR
      );
      setSelectedSnapshot(snapshotsWithPiTR[0]?.id || '');
    } else if (isNativeBackup) {
      setSelectedSnapshot(dateSnapshots[0]?.id || '');
    }
  }, [dateSnapshots, snapshotType, isNativeBackup]);

  return (
    <>
      {snapshotType === 'vm-based' && (
        <VmBasedSnapshots
          snapshots={snapshotsWithoutPiTR}
          entity={entity}
          selectedSnapshot={selectedSnapshot}
          setSelectedSnapshot={setSelectedSnapshot}
          vaults={vaults}
          mutateSnapshots={mutateSnapshots}
        />
      )}

      {isNativeBackup && (
        <NativeBackupSnapshotsRows
          snapshots={snapshotsWithoutPiTR}
          vaults={vaults}
          selectedSnapshot={selectedSnapshot}
          setSelectedSnapshot={setSelectedSnapshot}
          entity={entity}
          mutateSnapshots={mutateSnapshots}
        />
      )}

      {/* SnapshotsRows for native backup resources (excluding FSx, EFS, and Redshift) and non-native resources with restore wizard */}
      {restoreWizardFactory &&
        !isNativeBackup &&
        (!snapshotType || snapshotType === 'rds-based-snapshots') && (
          <div className='w-full overflow-auto'>
            <SnapshotsRows
              snapshots={snapshotsWithoutPiTR}
              vaults={vaults}
              selectedSnapshot={selectedSnapshot}
              setSelectedSnapshot={setSelectedSnapshot}
              entity={entity}
              restoreWizardFactory={restoreWizardFactory}
              mutateSnapshots={mutateSnapshots}
            />
          </div>
        )}
      {snapshotType === 'database-pitr' &&
        (isContinuousSupported ? (
          <ContinuousSnapshots
            resource={entity}
            selectedDate={dayjs.utc(selectedDate)}
            dateRanges={continuousSnapshots}
          />
        ) : (
          restoreWizardFactory && (
            <PitrSnapshots
              pitrSnapshots={snapshotsWithPiTR}
              vaults={vaults}
              selectedSnapshot={selectedSnapshot}
              setSelectedSnapshot={setSelectedSnapshot}
              entity={entity}
              restoreWizardFactory={restoreWizardFactory}
              mutateSnapshots={mutateSnapshots}
            />
          )
        ))}
    </>
  );
};

const NativeBackupSnapshotsRows = ({
  snapshots,
  vaults,
  selectedSnapshot,
  setSelectedSnapshot,
  entity,
  mutateSnapshots,
}: {
  snapshots: Snapshot[];
  vaults: BackupVault[];
  selectedSnapshot: string;
  setSelectedSnapshot: (snapshotId: string) => void;
  entity: InventoryResource;
  mutateSnapshots: MutateSnapshots;
}) => {
  const isFsxResourceType = entity.resourceType === ResourceType.AwsFsx;
  const isEfsResourceType = entity.resourceType === ResourceType.AwsEfs;
  const isRedshiftResourceType =
    entity.resourceType === ResourceType.AwsRedshift;
  const isDocumentdbOrNeptuneResourceType =
    entity.resourceType === ResourceType.AwsDocumentdb ||
    entity.resourceType === ResourceType.AwsNeptune;

  if (isFsxResourceType) {
    return (
      <FsxSnapshots
        snapshots={snapshots}
        entity={entity}
        selectedSnapshot={selectedSnapshot}
        setSelectedSnapshot={setSelectedSnapshot}
        vaults={vaults}
        mutateSnapshots={mutateSnapshots}
      />
    );
  }

  if (isEfsResourceType) {
    return (
      <EfsSnapshots
        snapshots={snapshots}
        entity={entity}
        selectedSnapshot={selectedSnapshot}
        setSelectedSnapshot={setSelectedSnapshot}
        vaults={vaults}
        mutateSnapshots={mutateSnapshots}
      />
    );
  }

  if (isRedshiftResourceType) {
    return (
      <RedshiftSnapshots
        snapshots={snapshots}
        entity={entity}
        selectedSnapshot={selectedSnapshot}
        setSelectedSnapshot={setSelectedSnapshot}
        vaults={vaults}
        mutateSnapshots={mutateSnapshots}
      />
    );
  }

  if (isDocumentdbOrNeptuneResourceType) {
    return (
      <NativeDbSnapshots
        snapshots={snapshots}
        entity={entity}
        selectedSnapshot={selectedSnapshot}
        setSelectedSnapshot={setSelectedSnapshot}
        vaults={vaults}
        mutateSnapshots={mutateSnapshots}
      />
    );
  }
};
